import React, { useState, useEffect } from 'react';
import { Box, Avatar } from '@mui/material';
import { getAllCompanyIssuers, updateIssuer } from '../../../services/apiservice';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import { useAuth } from '../../../contexts/authContext';
import CompanyIssuerEditModal from './CompanyIssuerEditModal';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

function CompanyIssuerList() {
  const { user } = useAuth();
  const issuerID = user.IssuerID;
  const { data: fetchedIssuers, loading } = useApiData(() => getAllCompanyIssuers(issuerID).then((res) => res.data), [issuerID]);
  const [issuers, setIssuers] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIssuer, setSelectedIssuer] = useState(null);

  useEffect(() => {
    if (fetchedIssuers) {
      setIssuers(fetchedIssuers);
    }
  }, [fetchedIssuers]);

  const handleEdit = (issuer) => {
    setSelectedIssuer(issuer);
    setModalOpen(true);
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedIssuer(null);
  };

  const handleSave = async (updatedIssuer) => {
    try {
      const response = await updateIssuer(updatedIssuer);
      if (response.success) {
        setIssuers((prevIssuers) =>
          prevIssuers.map((issuer) =>
            issuer.IssuerID === updatedIssuer.IssuerID ? { ...issuer, ...updatedIssuer } : issuer
          )
        );
        setSnackbarMessage('Issuer updated successfully!');
        setSnackbarSeverity('success');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const columns = [
    { key: 'IssuerID', label: 'Issuer ID', width: 100 },
    { key: 'IssuerName', label: 'Issuer Name', width: 200 },
    {
      key: 'repName',
      label: 'Representative Full Name',
      width: 200,
      render: (issuer) => (
        <Box display="flex" alignItems="center">
          <Avatar src={issuer.IssuerLogoURL} sx={{ width: 40, height: 40, marginRight: 2 }} />
          <span>{`${issuer.IssuerRepFirstName || ''} ${issuer.IssuerRepLastName || ''}`}</span>
        </Box>
      ),
    },
    { key: 'IssuerRepEmailID', label: 'Representative Email ID', width: 200 },
    { key: 'IssuerRepPhoneNumber', label: 'Representative Phone Number', width: 150 },
  ];

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Issuers List"
        columns={columns}
        rows={issuers}
        loading={loading}
        searchKeys={['IssuerName', (i) => `${i.IssuerRepFirstName || ''} ${i.IssuerRepLastName || ''}`, 'IssuerRepEmailID', 'IssuerRepPhoneNumber', 'IssuerWebsite']}
        rowKey="IssuerID"
        onEdit={handleEdit}
      />
      <CompanyIssuerEditModal
        open={modalOpen}
        onClose={handleModalClose}
        issuer={selectedIssuer}
        onSave={handleSave}
      />
    </>
  );
}

export default CompanyIssuerList;
